import { IAppContext } from 'App/AppContext';
import { IGraphInterface } from 'App/AppContextProvider';
import Joi from 'joi';
import { ListItem, ListItemVersion } from 'microsoft-graph';
import { TFunction } from 'i18next';
import Entity from 'models/entity';
import {
  getLinkEmbedURL,
  getLinkThumbnail,
  getLinkWebURL,
  isLinkValid,
  publishLink,
  setLinkMetaData,
} from 'services/Wrappers/linkContent/linkContentService';
import { IDriveItem, IDriveItemVersion, ISitePage } from 'services/Graph/SharepointInterfaces';
import { getLocalizedMessageOptions } from 'services/Localization/joiValidation';
import { areDifferent } from 'utils/array';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { newGuidNil } from 'utils/guid';
import { sortOnVersion } from 'utils/sorting';
import { getURLLastPart } from 'utils/url';
import Activity from './activity';
import { Approval, ApprovalState } from './approval';
import { Audience } from './audience/audience';
import Group from './group';
import { IOwner } from './owner';
import ResourceLinkChannel from './resourceLinkChannel';
import ResourceList, { ResourceListType } from './resourceList';
import Task from './tasks/task';
import { TeamsChannel } from './teamsChannel';
import { UserReadConfirmation } from './userReadConfirmation';

let newLinkId = -1;

//
// Word document detection
//
const isDocxFileName = (fileName: string | undefined): boolean => {
  return !!fileName?.toLowerCase().endsWith('.docx');
};

const stripQueryAndHash = (url: string | undefined): string => {
  if (!url) return '';

  return url.split(/[?#]/)[0];
};

// The file name a SharePoint viewer URL carries in its `file` query value, else the last path segment.
const getUrlFileName = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    const file = parsed.searchParams.get('file');
    if (file) return file;
    const last = getURLLastPart(parsed.pathname);

    return last ? decodeURIComponent(last) : undefined;
  } catch {
    return getURLLastPart(stripQueryAndHash(url));
  }
};

//
// Publishing states
//
export enum PublishingStateSharePoint {
  None = 0,
  Draft = 1,
  Published = 2,
}

export enum PublishingStateResourceLink {
  None = 0,
  ToBePublished = 3, // SharePoint indicates major version but there is no publication record
}

//Combined publishing state of SharePoint and Resource Link
export type ResourceLinkPublishingState = PublishingStateSharePoint | PublishingStateResourceLink;

//Create a constant array of the enum values to iterate over. Note that ordering matters here.
export const ResourceLinkPublishingStateOptions: readonly ResourceLinkPublishingState[] = [
  PublishingStateResourceLink.None,
  PublishingStateSharePoint.Draft,
  PublishingStateResourceLink.ToBePublished,
  PublishingStateSharePoint.Published,
];

export enum ResourceLinkCannotPublishReason {
  None = 0,
  IsWholeList = 1,
  ListTypeNotSupported = 2,
  MetadataNotEnabled = 3,
  IsNewLink = 4,
}

//
// Main class
//
export default class ResourceLink implements IOwner {
  linkId: number;

  listId: number;

  ownerId?: string;

  ownerRoleId?: string;

  listItemId?: string;

  driveItemId?: string;

  pageId?: string;

  externalId?: string;

  linkName: string;

  linkURL: string; //don't use to navigate: use getWebURL()

  linkURLFragment: string;

  publicationId?: number;

  pinned: boolean;

  linkTypeId?: number;

  readRequest: boolean;

  usedCount: number;

  entity?: Entity;

  valid?: boolean;

  isValidated: boolean;

  list: ResourceList;

  resourceLinkChannels?: ResourceLinkChannel[];

  userReadConfirmations?: UserReadConfirmation[];

  normIds?: number[];

  kbGroups?: KBTeam[];

  approvals?: Approval[];

  activity?: Activity;

  sourceDriveId?: string;

  //
  // Metadata
  //
  versionInfo: ResourceLinkVersion;

  versions: ResourceLinkVersion[];

  lastPublishedVersion: string | undefined;

  get publishingState(): ResourceLinkPublishingState {
    //
    // Calculate the publishing state based on the last published version and the current version info.
    // - the current version in SharePoint is a major version
    // - the latestPublishedVersion is lower than that, then the state is ToBePublished.
    // - the link can be published
    // Otherwise, use the publishing state from SharePoint.
    //
    // Note: we only return the ToBePublished state when we have both lastPublishedVersion and versionInfo data.
    // This introduces a corner case when the lastPublishedVersion is empty because there is actually none instead of not being loaded.
    // So for views where lastPublishedVersion should be known (retrieved from back-end), call setDefaultLastPublishedVersion() first.
    //
    // Note 2: State ToBePublished is not available for all subscriptions
    // Callers should adjust the state to Published accordingly based on feature availability.
    //
    if (this.lastPublishedVersion && this.versionInfo?.hasData()) {
      const isToBePublished =
        this.canPublish() &&
        ResourceLink.isMajorVersion(this.versionInfo.version) &&
        sortOnVersion(this.lastPublishedVersion, this.versionInfo.version) < 0;

      if (isToBePublished) {
        return PublishingStateResourceLink.ToBePublished;
      } else {
        return this.versionInfo.publishingState || PublishingStateResourceLink.None;
      }
    } else {
      //there is not enough data to calculate the publishing state
      return PublishingStateResourceLink.None;
    }
  }

  setDefaultLastPublishedVersion() {
    if (!this.lastPublishedVersion) {
      this.lastPublishedVersion = '0.0';
    }
  }

  constructor() {
    this.linkId = newLinkId;
    this.linkName = '';
    this.linkURL = '';
    this.linkURLFragment = '';
    this.usedCount = 0;
    this.list = new ResourceList();
    this.listId = 0;
    this.pinned = false;
    this.readRequest = false;
    this.isValidated = false;
    this.versions = [];
    this.versionInfo = new ResourceLinkVersion();
    newLinkId = newLinkId - 1;
  }

  isEqual(item: ResourceLink): boolean {
    //Do not compare data from SharePoint
    //- changed, versions, publishing state, validation
    //Do not compare approvals
    if (item.linkId !== this.linkId) return false;
    if (item.linkName !== this.linkName) return false;
    if (item.linkURL !== this.linkURL) return false;
    if (item.listId !== this.listId) return false;
    if (item.listItemId !== this.listItemId) return false;
    if (item.driveItemId !== this.driveItemId) return false;
    if (item.pageId !== this.pageId) return false;
    if (item.externalId !== this.externalId) return false;
    if (item.pinned !== this.pinned) return false;
    if (item.ownerId !== this.ownerId) return false;
    if (item.ownerRoleId !== this.ownerRoleId) return false;
    if (item.linkTypeId !== this.linkTypeId) return false;

    if (
      areDifferent(item.normIds, this.normIds, (a: number, b: number) => {
        return a === b;
      }) === true
    ) {
      return false;
    }

    return true;
  }

  clone(): ResourceLink {
    const newItem = new ResourceLink();
    newItem.linkId = this.linkId;
    newItem.linkName = this.linkName;
    newItem.linkURL = this.linkURL;
    newItem.usedCount = this.usedCount;
    newItem.listId = this.listId;
    newItem.listItemId = this.listItemId;
    newItem.driveItemId = this.driveItemId;
    newItem.list = this.list;
    newItem.pageId = this.pageId;
    newItem.externalId = this.externalId;
    newItem.pinned = this.pinned;
    newItem.valid = this.valid;
    newItem.isValidated = this.isValidated;
    newItem.approvals = this.approvals ? [...this.approvals] : undefined;
    newItem.versionInfo = this.versionInfo.clone();
    newItem.ownerId = this.ownerId;
    newItem.versions = [...this.versions];
    newItem.normIds = this.normIds ? [...this.normIds] : undefined;
    newItem.ownerRoleId = this.ownerRoleId;
    newItem.readRequest = this.readRequest;
    newItem.userReadConfirmations = this.userReadConfirmations ? [...this.userReadConfirmations] : undefined;
    newItem.linkTypeId = this.linkTypeId;
    newItem.sourceDriveId = this.sourceDriveId;

    return newItem;
  }

  isWholeList(): boolean {
    if (!this.driveItemId && !this.listItemId && !this.pageId && this.linkURL === 'list') return true;

    return false;
  }

  // Whether this link points to a Word document in a document library. The display name alone is not
  // reliable: the backend uniquifies a colliding link name by appending a counter AFTER the file name
  // (e.g. 'report.docx (2)'), so also test the file name in the link URL, which is the drive item's web
  // URL and always keeps the real file name last. Widening only: a false positive fails late in the
  // editor/export instead of hiding the action. A store-installed link keeps the real file name in the
  // viewer URL's `file` query value.
  isWordDocument(): boolean {
    if (this.isWholeList() || !this.list) return false;
    if (this.list.listType !== ResourceListType.DocumentLibrary) return false;

    return isDocxFileName(this.linkName) || isDocxFileName(this.getFileName());
  }

  // The name of the file behind the link: the file name in the link URL (see isWordDocument), else the
  // display name.
  getFileName(): string | undefined {
    return getUrlFileName(this.linkURL) ?? this.linkName;
  }

  setWholeList(list: ResourceList): boolean {
    this.list = list;
    this.listId = list.listId;
    this.linkName = list.name;
    this.linkURL = 'list';
    this.driveItemId = undefined;
    this.listItemId = undefined;
    this.pageId = undefined;

    return false;
  }

  validate(localizedFields: Record<string, string>): Joi.ValidationResult {
    if (this.isWholeList()) {
      //return a dummy validation result
      const schema: Joi.ObjectSchema = Joi.object({
        name: Joi.string().min(4).max(4),
      });

      return schema.validate({ url: this.linkURL }, { abortEarly: false });
    }

    const schema: Joi.ObjectSchema = Joi.object({
      name: Joi.string().min(1).max(512).required().label(localizedFields['name'] || 'name'),
      url: Joi.string().min(1).max(800).required().uri().label(localizedFields['url'] || 'url'),
      externalId: Joi.string().allow('').max(256).label(localizedFields['externalId'] || 'externalId'),
    }).prefs(getLocalizedMessageOptions());

    return schema.validate(
      { name: this.linkName.trim(), url: this.linkURL.trim(), externalId: this.externalId?.trim() ?? '' },
      { abortEarly: false },
    );
  }

  async getThumbnail(appContext: IAppContext): Promise<string> {
    return await getLinkThumbnail(this, appContext);
  }

  async publish(appContext: IAppContext): Promise<void> {
    return await publishLink(this, appContext);
  }

  canPublish(): boolean {
    return this.cannotPublishReason() === ResourceLinkCannotPublishReason.None;
  }

  /**
   * A category on a provider other than SharePoint reaches the MetadataNotEnabled branch
   * below: no such provider has a major/minor version model, so the server forces
   * enableMetaData off for them. That is what keeps publication, approvals and read
   * confirmations closed without every caller here needing the provider capabilities.
   */
  cannotPublishReason(): ResourceLinkCannotPublishReason {
    //note that content in virtual lists cannot actually be published
    //but we allow publishing for links in virtual lists for better user experience in trials
    //the back-end will skip any publish requests for virtual lists
    if (this.linkId < 0) return ResourceLinkCannotPublishReason.IsNewLink;
    if (this.isWholeList()) return ResourceLinkCannotPublishReason.IsWholeList;
    if (this.list.listType === ResourceListType.CustomList) return ResourceLinkCannotPublishReason.ListTypeNotSupported;
    if (this.list.listType === ResourceListType.WebURL) return ResourceLinkCannotPublishReason.ListTypeNotSupported;
    if (this.list.enableMetaData !== true) return ResourceLinkCannotPublishReason.MetadataNotEnabled;

    return ResourceLinkCannotPublishReason.None;
  }

  getMetaDataBatchRequest(): string | undefined {
    if (this.isWholeList()) {
      return undefined;
    }

    let request: string | undefined = undefined;
    if (this.list && this.list.enableMetaData === true && !this.list.isVirtual) {
      switch (this.list.listType) {
        case ResourceListType.DocumentLibrary:
          if (this.driveItemId && this.list.spDriveId) {
            request = `/drives/${this.list.spDriveId}/items/${this.driveItemId}/versions`;
          }
          break;
        case ResourceListType.SitePageLibrary:
          if (this.list.spSiteId && this.list.spListId && this.listItemId) {
            request = `sites/${this.list.spSiteId}/lists/${this.list.spListId}/items/${this.listItemId}/versions`;
          }
          break;
      }
    }

    return request;
  }

  applyMetaDataBatchResultListItem(versions: ListItemVersion[]) {
    if (!versions) return;
    this.versions = [];

    for (let idx = 0; idx < versions.length; idx++) {
      const version = versions[idx];
      if (version.id) {
        const newVersion = new ResourceLinkVersion();
        newVersion.id = version.id;
        newVersion.version = version.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fields: any = version.fields;
        if (fields && fields['Modified']) {
          newVersion.date = new Date(fields['Modified']);
        }
        this.versions.push(newVersion);
      }
    }

    if (this.versions.length > 0) {
      const latestVersion = this.versions[0];
      this.versionInfo = latestVersion;
    }
  }

  applyMetaDataBatchResultDriveItem(versions: IDriveItemVersion[]) {
    if (!versions) return;
    this.versions = [];

    for (let idx = 0; idx < versions.length; idx++) {
      const version = versions[idx];
      if (version.id) {
        const newVersion = new ResourceLinkVersion();
        newVersion.id = version.id;
        newVersion.date = version?.lastModifiedDateTime ? new Date(version?.lastModifiedDateTime) : new Date();
        newVersion.version = version.id;
        this.versions.push(newVersion);
      }
    }

    if (this.versions.length > 0) {
      const latestVersion = this.versions[0];
      this.versionInfo.date = latestVersion.date;
      this.versionInfo.version = latestVersion.id;
      this.versionInfo.publishingState = ResourceLink.isMajorVersion(this.versionInfo.version)
        ? PublishingStateSharePoint.Published
        : PublishingStateSharePoint.Draft;
    }
  }

  setMetaDataForDriveItem(item: IDriveItem) {
    if (item.publication) {
      this.versionInfo.version = item.publication.versionId ?? '';
      this.versionInfo.publishingState = ResourceLink.isMajorVersion(this.versionInfo.version)
        ? PublishingStateSharePoint.Published
        : PublishingStateSharePoint.Draft;
    }
    this.versionInfo.date = item?.lastModifiedDateTime ? new Date(item.lastModifiedDateTime) : undefined;
  }

  setMetaDataForPage(page: ISitePage) {
    if (page?.publishingState?.versionId) {
      this.versionInfo.id = page?.publishingState.versionId;
      this.versionInfo.date = page?.lastModifiedDateTime ? new Date(page?.lastModifiedDateTime) : new Date();
      this.versionInfo.version = page?.publishingState?.versionId;
      this.versionInfo.publishingState = ResourceLink.getPublishingStateFromSitePage(page?.publishingState?.level);
    }
  }

  static isMajorVersion(version: string | undefined): boolean {
    return version?.endsWith('.0') ?? false;
  }

  async setMetaData(
    graphInterfacePages: IGraphInterface | undefined,
    graphInterfaceDocuments: IGraphInterface | undefined,
    appContext: IAppContext,
  ): Promise<void> {
    return await setLinkMetaData(this, graphInterfacePages, graphInterfaceDocuments, appContext);
  }

  async getEmbedURL(appContext: IAppContext): Promise<string | undefined> {
    return await getLinkEmbedURL(this, appContext);
  }

  async getWebURL(appContext: IAppContext): Promise<string> {
    return await getLinkWebURL(this, appContext);
  }

  buildLinkURLFromListItem = (item: ListItem | undefined): string | undefined => {
    if (item && item.webUrl) {
      let url = item.webUrl.substring(0, item.webUrl.lastIndexOf('/'));
      url = url + '/DispForm.aspx?';
      url = url + 'ID=';
      url = url + item.id?.toString();

      return url;
    }

    return undefined;
  };

  async isValid(appContext: IAppContext): Promise<boolean | undefined> {
    return await isLinkValid(this, appContext);
  }

  static getPublishingStateFromSitePage = (state: string | undefined | null): PublishingStateSharePoint => {
    switch (state) {
      case 'published':
        return PublishingStateSharePoint.Published;
      case 'checkout':
      case 'draft':
        return PublishingStateSharePoint.Draft;
      default:
        return PublishingStateSharePoint.None;
    }
  };

  static getPublicationStateText(publishingState: ResourceLinkPublishingState | undefined, t: TFunction): string {
    //the states are hard coded by Microsoft Graph API
    switch (publishingState) {
      case PublishingStateSharePoint.Published:
        return t('sharepoint:PagePublicationState.Published');
      case PublishingStateResourceLink.ToBePublished:
        return t('sharepoint:PagePublicationState.ToBePublished');
      case PublishingStateSharePoint.Draft:
        return t('sharepoint:PagePublicationState.Draft');
      default:
        return '';
    }
  }

  getCannotPublishReasonText(t: TFunction): string {
    const reason = this.cannotPublishReason();
    const category = `${this.list.name} (${this.list.getListTypeText(t)})`;

    switch (reason) {
      case ResourceLinkCannotPublishReason.IsWholeList:
        return t('sharepoint:CannotPublishReason.IsWholeList', { category: category });
      case ResourceLinkCannotPublishReason.ListTypeNotSupported:
        return t('sharepoint:CannotPublishReason.ListTypeNotSupported', { category: category });
      case ResourceLinkCannotPublishReason.MetadataNotEnabled:
        return t('sharepoint:CannotPublishReason.MetadataNotEnabled', { category: category });
      case ResourceLinkCannotPublishReason.IsNewLink:
        return t('sharepoint:CannotPublishReason.IsNewLink', { category: category });
      default:
        return '';
    }
  }

  // Return true if there is at least one read confirmation
  getRC(): UserReadConfirmation | undefined {
    return this.userReadConfirmations && this.userReadConfirmations[0];
  }

  // Set a single read confirmation
  setRC(usr: UserReadConfirmation): void {
    this.userReadConfirmations = [usr];
  }

  // Return true if there is at least one read confirmation
  hasRC(): boolean {
    return (this.userReadConfirmations && this.userReadConfirmations.length > 0) ?? false;
  }

  // Return true if there is at least one unread read confirmation
  hasRCUnread(): boolean {
    return (
      (this.userReadConfirmations &&
        this.userReadConfirmations.length > 0 &&
        this.userReadConfirmations[0].readConfirmed === undefined) ??
      false
    );
  }

  // Return true if there is at least one read confirmation marked as read
  hasRCRead(): boolean {
    return (
      (this.userReadConfirmations &&
        this.userReadConfirmations.length > 0 &&
        this.userReadConfirmations[0].readConfirmed !== undefined) ??
      false
    );
  }
}

//
// Helper classes
//
export class ResourceLinkVersion {
  id: string;

  date?: Date;

  version: string;

  publishingState?: PublishingStateSharePoint;

  constructor() {
    this.id = '';
    this.version = '';
  }

  hasData(): boolean {
    return this.version !== '';
  }

  clone(): ResourceLinkVersion {
    const newVersion = new ResourceLinkVersion();
    newVersion.id = this.id;
    newVersion.date = this.date;
    newVersion.version = this.version;
    newVersion.publishingState = this.publishingState;

    return newVersion;
  }
}

export interface ILinkRow {
  item: ResourceLink;
  isBusy: boolean;
  key?: string; //optional composite key for the row
  audience?: Audience; //optional audience id if the row is shown in an audience context
}

export class EntityLink extends Entity {
  pinned: boolean;

  linkURLFragment?: string;

  constructor() {
    super();
    this.pinned = false;
  }
}

export class KBTeam {
  group: Group;

  channels: TeamsChannel[];

  links: ResourceLink[];

  hash: string;

  constructor() {
    this.group = new Group(newGuidNil());
    this.channels = [];
    this.links = [];
    this.hash = newGuidNil();
  }

  setHash() {
    this.hash = this.group.id + '#' + this.channels.map((c) => c.channelId ?? newGuidNil()).join(',');
  }
}

export enum PublicationResultStatus {
  None = 0,
  Success = 1,
  Failed = 9,
}

export class PublicationResult {
  publicationId: number;

  created?: Date;

  createdById?: string;

  linkId: number;

  link?: ResourceLink;

  status?: PublicationResultStatus;

  result?: string;

  oldVersion?: string;

  newVersion?: string;

  lastModified?: Date;

  readConfirmationsSend?: number;

  smallChange?: boolean;

  approvalCompleted?: Date;

  approvalState?: ApprovalState;

  approvers?: string[];

  approvalTask?: Task;

  constructor() {
    this.publicationId = 0;
    this.linkId = 0;
  }

  getPublishResultStatusText(t: TFunction): string {
    if (this.status === PublicationResultStatus.Success) {
      return t('sharepoint:PublicationStatus.Success');
    }

    switch (this.result) {
      case ControllerExceptionCodes.Code_General_NotAuthorized:
        return t('sharepoint:PublicationStatus.NoPermissions');
      case ControllerExceptionCodes.Code_Graph_FileLocked:
        return t('sharepoint:PublicationStatus.Locked');
      default:
        return t('sharepoint:PublicationStatus.Failed');
    }
  }
}
